'use client';

/**
 * <DslView> — renders a `DslSpec` + KV snapshot to React.
 *
 * Used by:
 *   - workspace dock tiles (replaces the static lucide icon),
 *   - mobile compact mode (collapsed widget shows the projection
 *     instead of the full iframe),
 *   - the standalone widget-projection preview surface inside the
 *     Discover panel.
 *
 * All binding evaluation is performed via the allowlist evaluator in
 * `lib/workspaces/widget-projection.ts`. Nothing reaches React props
 * unsanitised — `interpolate` returns a string, never a node, so
 * `dangerouslySetInnerHTML` is never used.
 */

import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  evalExpr,
  interpolate,
  type DslNode,
  type DslSpec,
  type DslAction,
  type DslColor,
} from '@/lib/workspaces/widget-projection';
import { useWorkspaceBus } from '@/lib/workspaces/use-workspace-bus';

export interface DslViewProps {
  spec: DslSpec;
  /** KV snapshot the binding evaluator reads from `kv.*`. */
  kv: Record<string, unknown>;
  /**
   * When set, the component re-fetches `/api/items/:itemId/projection`
   * whenever the workspace bus emits a relevant frame. Pass the parent
   * widget's `itemId` + `workspaceId` to enable live updates. Omit for
   * static previews.
   */
  liveSource?: { workspaceId: string; itemId: string };
  /**
   * Handler for buttons + actions. The host wires this — typically to
   * `host.rpc('kv.set', ...)` for `kv.set` actions, `window.open(...)`
   * for `open` actions, and a workspace-bus emit for `widget.event`.
   * If unset, actions are no-ops.
   */
  onAction?: (action: DslAction, scope: Record<string, unknown>) => void;
  className?: string;
  compact?: boolean;
}

export function DslView(props: DslViewProps) {
  const [snapshot, setSnapshot] = useState<{ spec: DslSpec; kv: Record<string, unknown> }>({
    spec: props.spec,
    kv: props.kv,
  });

  // When the widget is wired to a `liveSource`, the props.spec /
  // props.kv are placeholders that only matter on cold-start — the
  // initial fetch below and the workspace-bus refresh own the
  // snapshot from then on. Mirroring identity changes back into
  // `snapshot` would otherwise clobber the freshly-fetched live tile
  // on every parent re-render (e.g. dragging the widget triggers a
  // store update → content-node re-renders → a fresh `kv={{}}`
  // reference flows in → this effect snaps the snapshot back to the
  // placeholder; the live tile blinks in for ~300ms then disappears).
  //
  // For static previews (no liveSource) we DO want to follow prop
  // changes so the parent can swap in a different spec.
  const hasLiveSource = !!props.liveSource;
  useEffect(() => {
    if (hasLiveSource) return;
    setSnapshot({ spec: props.spec, kv: props.kv });
  }, [hasLiveSource, props.spec, props.kv]);

  // Initial fetch when `liveSource` is provided. Without this the
  // widget renders with whatever KV came in via props (typically `{}`
  // when the parent doesn't have the values yet) and then waits for
  // a bus event to refresh — meaning a projection-only widget that
  // nothing is poking sits forever with empty bindings (e.g. the
  // weather tile rendering as three blank lines). The bus listener
  // below still drives live updates after this initial hydration.
  const liveSourceItemId = props.liveSource?.itemId;
  const liveSourceWorkspaceId = props.liveSource?.workspaceId;
  useEffect(() => {
    if (!liveSourceItemId) return;
    let alive = true;
    const url = `/api/items/${liveSourceItemId}/projection`;
    fetch(url, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!alive) return;
        if (
          data &&
          data.spec &&
          typeof data.kv === 'object' &&
          data.kv !== null
        ) {
          setSnapshot({ spec: data.spec, kv: data.kv });
        }
      })
      .catch(() => {
        // Silent — props.spec / props.kv stay on screen.
      });
    return () => {
      alive = false;
    };
  }, [liveSourceItemId, liveSourceWorkspaceId]);

  // Live updates: when the parent widget's KV changes, re-fetch the
  // projection. We could re-evaluate locally for inline specs, but
  // re-fetching keeps function-backed projections (which aggregate
  // KV server-side) consistent. The endpoint is ETag-cached.
  useWorkspaceBus(
    {
      workspaceId: props.liveSource?.workspaceId ?? '',
      itemId: props.liveSource?.itemId,
      kinds: ['kv'],
    },
    () => {
      if (!props.liveSource) return;
      const url = `/api/items/${props.liveSource.itemId}/projection`;
      fetch(url, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (
            data &&
            data.spec &&
            typeof data.kv === 'object' &&
            data.kv !== null
          ) {
            setSnapshot({ spec: data.spec, kv: data.kv });
          }
        })
        .catch(() => {
          // Silent — last good snapshot stays on screen.
        });
    },
  );

  const baseScope: Record<string, unknown> = { kv: snapshot.kv };
  const bgClass = props.spec.background ? colorBg(props.spec.background) : '';
  return (
    <div className={cn('w-full h-full flex flex-col text-foreground', bgClass, props.className)}>
      <RenderNode
        node={snapshot.spec.root}
        scope={baseScope}
        onAction={props.onAction}
        compact={props.compact}
      />
    </div>
  );
}

function RenderNode(p: {
  node: DslNode;
  scope: Record<string, unknown>;
  onAction?: DslViewProps['onAction'];
  compact?: boolean;
}): React.ReactElement | null {
  const { node, scope, onAction, compact } = p;
  if ('if' in node && typeof node.if === 'string') {
    if (!evalExpr(node.if, scope)) return null;
  }
  switch (node.type) {
    case 'text': {
      const value = interpolate(node.value, scope);
      return (
        <span
          className={cn(
            'inline-block',
            sizeClass(node.size),
            weightClass(node.weight),
            colorText(node.color),
            alignClass(node.align),
          )}
        >
          {value}
        </span>
      );
    }
    case 'image': {
      return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={node.src}
          alt={node.alt ?? ''}
          width={node.width}
          height={node.height}
          className={cn(
            'object-cover',
            node.rounded === 'full' && 'rounded-full',
            node.rounded === 'md' && 'rounded-md',
            node.rounded === 'sm' && 'rounded-sm',
          )}
        />
      );
    }
    case 'button': {
      return (
        <button
          type="button"
          onClick={() => onAction?.(node.action, scope)}
          className={cn(
            'inline-flex items-center justify-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
            node.variant === 'primary' &&
              'bg-primary text-primary-foreground hover:bg-primary/90',
            node.variant === 'secondary' &&
              'bg-secondary text-secondary-foreground hover:bg-secondary/90',
            (!node.variant || node.variant === 'ghost') &&
              'text-foreground hover:bg-accent',
          )}
        >
          {node.label}
        </button>
      );
    }
    case 'toggle': {
      const value = !!evalExpr(node.bind, scope);
      return (
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={value}
            onChange={(e) =>
              onAction?.({ type: 'kv.set', key: node.bind, value: e.target.checked }, scope)
            }
            className="h-4 w-4 rounded border-input"
          />
          {node.label && <span>{node.label}</span>}
        </label>
      );
    }
    case 'input': {
      const value = evalExpr(node.bind, scope);
      return (
        <input
          type="text"
          value={value == null ? '' : String(value)}
          placeholder={node.placeholder}
          onChange={(e) =>
            onAction?.({ type: 'kv.set', key: node.bind, value: e.target.value }, scope)
          }
          className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
        />
      );
    }
    case 'list': {
      const items = evalExpr(node.items, scope);
      if (!Array.isArray(items) || items.length === 0) {
        if (node.empty) {
          return (
            <RenderNode node={node.empty} scope={scope} onAction={onAction} compact={compact} />
          );
        }
        return null;
      }
      return (
        <>
          {items.map((item, idx) => (
            <RenderNode
              key={idx}
              node={node.each}
              scope={{ ...scope, $item: item, $index: idx }}
              onAction={onAction}
              compact={compact}
            />
          ))}
        </>
      );
    }
    case 'spark': {
      const values = evalExpr(node.bind, scope);
      if (!Array.isArray(values) || values.length === 0) return null;
      const nums = values.map((v) => Number(v) || 0);
      const min = Math.min(...nums);
      const max = Math.max(...nums);
      const range = max - min || 1;
      const h = node.height ?? 24;
      const w = 100;
      const points = nums
        .map((v, i) => {
          const x = (i / Math.max(1, nums.length - 1)) * w;
          const y = h - ((v - min) / range) * h;
          return `${x.toFixed(2)},${y.toFixed(2)}`;
        })
        .join(' ');
      return (
        <svg
          viewBox={`0 0 ${w} ${h}`}
          className="w-full"
          style={{ height: h }}
          aria-hidden="true"
        >
          <polyline
            points={points}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      );
    }
    case 'vstack':
    case 'hstack': {
      const cls = node.type === 'vstack' ? 'flex flex-col' : 'flex flex-row';
      return (
        <div
          className={cn(cls, 'items-stretch')}
          style={{
            gap: node.gap ?? 8,
            padding: node.padding,
          }}
        >
          {node.children.map((c, i) => (
            <RenderNode key={i} node={c} scope={scope} onAction={onAction} compact={compact} />
          ))}
        </div>
      );
    }
    case 'grid': {
      return (
        <div
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${Math.max(1, Math.floor(node.columns))}, minmax(0, 1fr))`,
            gap: node.gap ?? 8,
          }}
        >
          {node.children.map((c, i) => (
            <RenderNode key={i} node={c} scope={scope} onAction={onAction} compact={compact} />
          ))}
        </div>
      );
    }
  }
}

function sizeClass(size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'): string {
  switch (size) {
    case 'xs':
      return 'text-[10px]';
    case 'sm':
      return 'text-xs';
    case 'lg':
      return 'text-base';
    case 'xl':
      return 'text-lg';
    case 'md':
    default:
      return 'text-sm';
  }
}
function weightClass(weight?: 'regular' | 'medium' | 'bold'): string {
  if (weight === 'bold') return 'font-bold';
  if (weight === 'medium') return 'font-medium';
  return 'font-normal';
}
function alignClass(align?: 'start' | 'center' | 'end'): string {
  if (align === 'center') return 'text-center w-full';
  if (align === 'end') return 'text-right w-full';
  return '';
}
function colorText(color?: DslColor): string {
  switch (color) {
    case 'muted':
      return 'text-muted-foreground';
    case 'primary':
      return 'text-primary';
    case 'success':
      return 'text-emerald-500';
    case 'warning':
      return 'text-amber-500';
    case 'danger':
      return 'text-rose-500';
    case 'foreground':
    default:
      return 'text-foreground';
  }
}
function colorBg(color: DslColor): string {
  switch (color) {
    case 'muted':
      return 'bg-muted';
    case 'primary':
      return 'bg-primary';
    case 'success':
      return 'bg-emerald-500/10';
    case 'warning':
      return 'bg-amber-500/10';
    case 'danger':
      return 'bg-rose-500/10';
    case 'foreground':
      return 'bg-foreground/10';
    default:
      return '';
  }
}
